import { z } from 'zod';
import { LEGACY_OBJECT_COLORS, LEGACY_OBJECT_COLOR_NAMES, type LegacyObjectColor } from './colors-legacy';

/**
 * The neutral object palette (plan §9.1): identity colours of objects, apps and blueprint tiles.
 * Names describe the hue, never a domain; the values live in the UI kit's tokens (`--obj-<name>`,
 * light and dark). Owner: design-system.
 */
export const OBJECT_COLORS = [
  'indigo',
  'blue',
  'teal',
  'green',
  'amber',
  'orange',
  'rose',
  'violet',
  'slate',
] as const;
export type NeutralObjectColor = (typeof OBJECT_COLORS)[number];

/** The colour of objects that do not choose one. */
export const DEFAULT_OBJECT_COLOR: NeutralObjectColor = 'slate';

/**
 * An object colour. Parsing accepts the neutral names and (until wave 3) the names stored before the
 * neutral palette, and returns the neutral name of the same hue (`normalizeObjectColor`): blueprint
 * packages on disk and older API clients still send those names, and everything written from now on
 * is neutral, so a blueprint's base spec and the stored object always agree. Values read from the
 * database are not parsed: rows written before the data migration `design-system-object-colours`
 * may still hold an old name, which the UI renders through the token aliases. Pickers offer
 * `OBJECT_COLORS` only. (`.overwrite` keeps the schema an enum: JSON Schema lists every accepted name.)
 */
export const ObjectColor = z
  .enum([...OBJECT_COLORS, ...LEGACY_OBJECT_COLOR_NAMES] as [
    NeutralObjectColor | LegacyObjectColor,
    ...(NeutralObjectColor | LegacyObjectColor)[],
  ])
  .overwrite((color) => normalizeObjectColor(color));
export type ObjectColor = z.infer<typeof ObjectColor>;
export type { LegacyObjectColor };
export { LEGACY_OBJECT_COLORS };

const NEUTRAL = new Set<string>(OBJECT_COLORS);

export function isNeutralObjectColor(value: unknown): value is NeutralObjectColor {
  return typeof value === 'string' && NEUTRAL.has(value);
}

/** The neutral colour a stored name renders with (unknown names fall back to the default). */
export function normalizeObjectColor(value: unknown): NeutralObjectColor {
  if (isNeutralObjectColor(value)) return value;
  if (typeof value === 'string' && Object.hasOwn(LEGACY_OBJECT_COLORS, value))
    return LEGACY_OBJECT_COLORS[value as LegacyObjectColor];
  return DEFAULT_OBJECT_COLOR;
}

/** Icon names available in the UI kit's icon set (packages/ui/src/icons.ts). */
export const OBJECT_ICONS = [
  'building',
  'person',
  'currency',
  'calendar',
  'mail',
  'box',
  'list',
  'task',
  'badge',
  'briefcase',
  'file',
] as const;
export const ObjectIcon = z.enum(OBJECT_ICONS);
export type ObjectIcon = z.infer<typeof ObjectIcon>;

/** Hex colors for member avatars and stage dots. */
export const HexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
